import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { accountingAPI } from '../../../api';
import Layout from '../../../components/Layout';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import { Card, CardContent } from '../../../components/ui/Card';
import { Badge } from '../../../components/ui/Badge';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '../../../components/ui/Table';
import { Plus, Eye, RotateCcw, Loader2, Calendar } from 'lucide-react';
import { useToast } from '../../../hooks/useToast';

const toInputDate = (d) => new Date(d).toISOString().split('T')[0];
const formatDate = (d) => new Date(d).toLocaleDateString('en-GB');

export default function JournalList() {
  const navigate = useNavigate();
  const { success, error: showError, ToastContainer } = useToast();
  
  const [journals, setJournals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filters, setFilters] = useState({
    startDate: '',
    endDate: '',
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 50,
    total: 0,
    totalPages: 0,
  });

  useEffect(() => {
    fetchJournals();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.page, filters]);

  const fetchJournals = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await accountingAPI.getJournals({
        startDate: filters.startDate || undefined,
        endDate: filters.endDate || undefined,
        page: pagination.page,
        limit: pagination.limit,
      });
      setJournals(data.journals || []);
      setPagination(prev => ({
        ...prev,
        total: data.pagination?.total || 0,
        totalPages: data.pagination?.totalPages || 0,
      }));
    } catch (err) {
      setError(err.message || 'Failed to load journals');
      console.error('Error fetching journals:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleReverse = async (journalId) => {
    if (!confirm('Are you sure you want to reverse this journal entry? This will create a reversal entry.')) {
      return;
    }

    try {
      await accountingAPI.reverseJournal(journalId, 'Manual reversal');
      success('Success', 'Journal entry reversed successfully');
      fetchJournals();
    } catch (err) {
      showError('Error', err.message || 'Failed to reverse journal');
    }
  };

  const handleApplyFilters = () => {
    setPagination(prev => ({ ...prev, page: 1 }));
    fetchJournals();
  };

  const handleClearFilters = () => {
    setFilters({ startDate: '', endDate: '' });
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  return (
    <>
      <ToastContainer />
      <Layout>
        {/* Header */}
        <div className="bg-[var(--ds-surface-panel)] border-b border-[var(--ds-border-subtle)] shadow-[var(--ds-shadow-card)]">
          <div className="max-w-7xl mx-auto px-6 py-6">
            <div className="md:flex md:items-center md:justify-between">
              <div className="flex-1 min-w-0">
                <h1 className="text-display text-[var(--ds-text-primary)] font-bold">
                  Manual Journal Entries
                </h1>
                <p className="mt-1 text-meta text-[var(--ds-text-secondary)]">
                  Create and manage manual journal entries
                </p>
              </div>
              <div className="mt-4 flex md:mt-0 md:ml-4 gap-3">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate('/tenant/reports')}
                >
                  ← Back to Reports
                </Button>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => navigate('/tenant/reports/journals/new')}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  New Journal Entry
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Filters */}
          <Card className="mb-6">
            <CardContent className="p-4">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div>
                  <label className="block text-meta font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Start Date
                  </label>
                  <Input
                    type="date"
                    value={filters.startDate}
                    onChange={(e) => setFilters({ ...filters, startDate: e.target.value })}
                    className="h-8"
                  />
                </div>
                <div>
                  <label className="block text-meta font-medium text-gray-700 dark:text-gray-300 mb-1">
                    End Date
                  </label>
                  <Input
                    type="date"
                    value={filters.endDate}
                    onChange={(e) => setFilters({ ...filters, endDate: e.target.value })}
                    className="h-8"
                  />
                </div>
                <div className="flex items-end gap-2">
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={handleApplyFilters}
                    className="flex-1"
                  >
                    Apply
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleClearFilters}
                    className="flex-1"
                  >
                    Clear
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Error */}
          {error && (
            <div className="bg-[var(--ds-status-danger-bg)] border border-[var(--ds-status-danger)]/30 text-[var(--ds-status-danger)] px-4 py-3 rounded-[var(--ds-radius-md)] mb-4">
              {error}
            </div>
          )}

          {/* Journals List */}
          <Card>
            {loading ? (
              <CardContent className="p-8 text-center">
                <Loader2 className="h-8 w-8 animate-spin mx-auto text-[var(--ds-accent-primary)]" />
                <p className="mt-2 text-xs text-[var(--ds-text-muted)]">Loading journals...</p>
              </CardContent>
            ) : (
              <CardContent className="p-6">
                {journals.length === 0 ? (
                  <div className="text-center py-12">
                    <p className="text-xs text-[var(--ds-text-muted)] mb-4">No journal entries found</p>
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={() => navigate('/tenant/reports/journals/new')}
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Create First Journal Entry
                    </Button>
                  </div>
                ) : (
                  <>
                    <Table>
                      <TableHeader>
                        <TableRow isHeader>
                          <TableHead>Journal #</TableHead>
                          <TableHead>Date</TableHead>
                          <TableHead>Reference</TableHead>
                          <TableHead>Description</TableHead>
                          <TableHead align="center">Status</TableHead>
                          <TableHead>Created By</TableHead>
                          <TableHead align="right">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {journals.map((journal) => (
                          <TableRow key={journal.id}>
                            <TableCell>
                              <span className="font-mono">{journal.journalNumber}</span>
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-2">
                                <Calendar className="h-4 w-4 text-[var(--ds-text-muted)]" />
                                {formatDate(journal.journalDate)}
                              </div>
                            </TableCell>
                            <TableCell>{journal.reference || '-'}</TableCell>
                            <TableCell>{journal.description || '-'}</TableCell>
                            <TableCell align="center">
                              <Badge
                                variant={journal.isReversed ? 'neutral' : 'success'}
                              >
                                {journal.isReversed ? 'Reversed' : 'Active'}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              {journal.createdBy ? `${journal.createdBy.firstName} ${journal.createdBy.lastName}` : '-'}
                            </TableCell>
                            <TableCell align="right">
                              <div className="flex justify-end gap-2">
                                <Button
                                  variant="outline"
                                  size="icon-sm"
                                  onClick={() => navigate(`/tenant/reports/journals/${journal.id}`)}
                                  title="View Details"
                                >
                                  <Eye className="h-4 w-4" />
                                </Button>
                                {!journal.isReversed && (
                                  <Button
                                    variant="outline"
                                    size="icon-sm"
                                    onClick={() => handleReverse(journal.id)}
                                    title="Reverse Entry"
                                  >
                                    <RotateCcw className="h-4 w-4" />
                                  </Button>
                                )}
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>

                    {/* Pagination */}
                    {pagination.totalPages > 1 && (
                      <div className="mt-6 flex items-center justify-between">
                        <div className="text-xs text-[var(--ds-text-secondary)]">
                          Showing {((pagination.page - 1) * pagination.limit) + 1} to {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} entries
                        </div>
                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                            disabled={pagination.page === 1}
                          >
                            Previous
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                            disabled={pagination.page >= pagination.totalPages}
                          >
                            Next
                          </Button>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </CardContent>
            )}
          </Card>
        </div>
      </Layout>
    </>
  );
}
